import type {IMaterialData} from "@cm/material-nodes"
import {keyForMaterialData} from "@cm/material-nodes"
import {Matrix4} from "@cm/math"
import type {SceneNodes} from "#template/interfaces/scene-object"

export type SceneEffect = "geometry" | "camera" | "lights" | "environment" | "scene options" | "render settings"
type SceneNodeCategory = SceneEffect | "postProcessing" | "ignore"

// Exhaustive on purpose: a new scene node type does not compile until someone decides whether it can change geometry.
const categoryByType: Record<SceneNodes.SceneNode["type"], SceneNodeCategory> = {
    Mesh: "geometry",
    Seam: "geometry",
    Camera: "camera",
    Environment: "environment",
    AreaLight: "lights",
    PointLight: "lights",
    SunLight: "lights",
    LightPortal: "lights",
    SceneOptions: "scene options",
    RenderSettings: "render settings",
    // Render jobs store raw passes and post-processing runs on download, so it never changes what a layer render contains.
    RenderPostProcessingSettings: "postProcessing",
    MeshCurveControl: "ignore",
    DimensionGuides: "ignore",
    WireframeMesh: "ignore",
    Rectangle: "ignore",
    Cuboid: "ignore",
    Point: "ignore",
    Annotation: "ignore",
    PreloadMaterial: "ignore",
    DataObjectReference: "ignore",
    Grid: "ignore",
}

export type SceneDifference = {
    geometry: boolean
    scene: boolean
    postProcessing: boolean
    effects: Set<SceneEffect>
    reasons: Set<string>
    onlyInBase: string[]
    onlyInOther: string[]
    // `<mesh id>#<slot>` of every material slot whose material differs.
    materialSlots: Set<string>
    materialObjects: Set<string>
}

function objectName(node: SceneNodes.SceneNode) {
    const name = node.path.at(-1)?.parameters.name
    return typeof name === "string" && name !== "" ? name : String(node.id)
}

const geometryReasonByType: Partial<Record<SceneNodes.SceneNode["type"], string>> = {
    Mesh: "objects added or removed",
    Seam: "seams",
}

// Material graph ids identify a graph revision, as in keyForMaterialData, so the hash of a displacement subgraph can be kept.
const displacementSignatures = new Map<string, string | undefined>()

// Only the displacement subgraph moves geometry: swapping the colour of a displacing material is still a material swap.
function displacementSignature(material: IMaterialData): string | undefined {
    const {materialGraph} = material
    if (displacementSignatures.has(materialGraph.uniqueId)) return displacementSignatures.get(materialGraph.uniqueId)
    const rootNode = materialGraph.rootNode
    const {inputs, settings} = rootNode?.parameters ?? {inputs: undefined, settings: undefined}
    const signature =
        inputs?.displacement === undefined || settings?.displacementEnabled === false
            ? undefined
            : JSON.stringify([inputs.displacement.getHash(), settings?.displacement])
    displacementSignatures.set(materialGraph.uniqueId, signature)
    return signature
}

const meshReasonByField: Record<string, string> = {
    abstract: "mesh data",
    reified: "mesh data",
    parent: "hierarchy",
    isDecal: "visibility",
    visibleDirectly: "visibility",
    visibleInReflections: "visibility",
    visibleInRefractions: "visibility",
    displacement: "displacement settings",
    displacementImage: "displacement",
    displacingMaterials: "displacing materials",
}

function meshGeometryFields(mesh: SceneNodes.Mesh): Record<string, unknown> {
    const {displacementImage, cryptoMatteObjectName: _object, cryptoMatteAssetName: _asset, ...displacement} = mesh.meshRenderSettings
    const displacingMaterials: [number, string][] = []
    for (const [slot, material] of mesh.materialMap) {
        const signature = material ? displacementSignature(material) : undefined
        if (signature !== undefined) displacingMaterials.push([slot, signature])
    }
    return {
        abstract: mesh.completeMeshData?.abstract?.contentHash,
        reified: mesh.completeMeshData?.reified?.contentHash,
        parent: mesh.parent?.id,
        isDecal: mesh.isDecal,
        visibleDirectly: mesh.visibleDirectly,
        visibleInReflections: mesh.visibleInReflections,
        visibleInRefractions: mesh.visibleInRefractions,
        displacement: normalize(displacement),
        displacementImage: displacementImage?.imageNode.hash,
        displacingMaterials: displacingMaterials.sort(([a], [b]) => a - b),
    }
}

function meshMaterialKeys(mesh: SceneNodes.Mesh): Map<number, string | null> {
    const keys = new Map<number, string | null>()
    for (const [slot, material] of mesh.materialMap) keys.set(slot, material ? keyForMaterialData(material) : null)
    return keys
}

function normalize(root: unknown): unknown {
    const seen = new Set<object>()
    const visit = (value: unknown, depth: number): unknown => {
        if (value === null || value === undefined || typeof value === "number" || typeof value === "string" || typeof value === "boolean") return value
        if (typeof value === "function" || typeof value === "symbol" || typeof value === "bigint") return undefined
        const object = value as object
        if (object instanceof Matrix4) return [...object.elements]
        if (ArrayBuffer.isView(object)) return `<${object.byteLength} bytes>`
        if (depth > 6 || seen.has(object)) return "<…>"
        if (depth > 0 && "type" in object && "id" in object && "path" in object) return `#${String(object.id)}`
        seen.add(object)
        if (object instanceof Map) return [...object].map(([k, v]) => [visit(k, depth + 1), visit(v, depth + 1)])
        if (Array.isArray(object)) return object.map((item) => visit(item, depth + 1))
        return Object.fromEntries(Object.keys(object).map((key) => [key, visit((object as Record<string, unknown>)[key], depth + 1)]))
    }
    return visit(root, 0)
}

// Positions from the connection solver differ between compiles of the same configuration by its convergence accuracy.
const relativeTolerance = 1e-4

function nearlyEqual(a: number, b: number) {
    return a === b || Math.abs(a - b) <= relativeTolerance * Math.max(1, Math.abs(a), Math.abs(b))
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function differingPaths(a: unknown, b: unknown, path = "", out = new Set<string>()): Set<string> {
    if (typeof a === "number" && typeof b === "number") {
        if (!nearlyEqual(a, b)) out.add(path)
    } else if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) out.add(path)
        else a.forEach((item, index) => differingPaths(item, b[index], path, out))
    } else if (isRecord(a) && isRecord(b)) {
        for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
            differingPaths(a[key], b[key], path.split(".").length >= 2 ? path : path ? `${path}.${key}` : key, out)
        }
    } else if (a !== b) {
        out.add(path)
    }
    return out
}

function transformChange(a: Matrix4, b: Matrix4) {
    const delta = Math.max(...a.elements.map((value, index) => (nearlyEqual(value, b.elements[index]) ? 0 : Math.abs(value - b.elements[index]))))
    return delta === 0 ? undefined : `transform (Δ ≤ 1e${Math.ceil(Math.log10(delta))})`
}

export function compareScenes(base: readonly SceneNodes.SceneNode[], other: readonly SceneNodes.SceneNode[]): SceneDifference {
    const difference: SceneDifference = {
        geometry: false,
        scene: false,
        postProcessing: false,
        effects: new Set(),
        reasons: new Set(),
        onlyInBase: [],
        onlyInOther: [],
        materialSlots: new Set(),
        materialObjects: new Set(),
    }
    const mark = (category: SceneNodeCategory, type: SceneNodes.SceneNode["type"], detail?: string) => {
        if (category === "ignore") return
        if (category === "postProcessing") {
            difference.postProcessing = true
            return
        }
        difference.effects.add(category)
        if (category === "geometry") difference.geometry = true
        else difference.scene = true
        const reason = geometryReasonByType[type] ?? category
        difference.reasons.add(detail ? `${reason}: ${detail}` : reason)
    }
    const byId = (nodes: readonly SceneNodes.SceneNode[]) =>
        new Map(nodes.filter((node) => categoryByType[node.type] !== "ignore").map((node) => [String(node.id), node]))
    const baseById = byId(base)
    const otherById = byId(other)

    for (const id of new Set([...baseById.keys(), ...otherById.keys()])) {
        const a = baseById.get(id)
        const b = otherById.get(id)
        if (!a || !b || a.type !== b.type) {
            const type = (a ?? b)!.type
            mark(categoryByType[type], type)
            if (categoryByType[type] !== "postProcessing") (a ? difference.onlyInBase : difference.onlyInOther).push(id)
            continue
        }
        if (a.type === "Mesh" && b.type === "Mesh") {
            const reasons = [...differingPaths(meshGeometryFields(a), meshGeometryFields(b))].map((path) => meshReasonByField[path.split(".")[0]])
            const transform = transformChange(a.transform, b.transform)
            if (transform) reasons.push(transform)
            for (const reason of reasons) {
                difference.geometry = true
                difference.effects.add("geometry")
                difference.reasons.add(reason)
            }
            const aKeys = meshMaterialKeys(a)
            const bKeys = meshMaterialKeys(b)
            for (const slot of new Set([...aKeys.keys(), ...bKeys.keys()]))
                if (aKeys.get(slot) !== bKeys.get(slot)) {
                    difference.materialSlots.add(`${id}#${slot}`)
                    difference.materialObjects.add(objectName(a))
                }
            continue
        }
        const paths = differingPaths(normalize(a), normalize(b))
        if (paths.size > 0) mark(categoryByType[a.type], a.type, [...paths].sort().join(", "))
    }
    return difference
}
